import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Calendar, Users, TrendingUp, Plus, LogOut } from 'lucide-react';
import { API_BASE_URL } from '../../config/environment';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [location, setLocation] = useState('all');
  const [loading, setLoading] = useState(true);
  const [adminUser, setAdminUser] = useState(null);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');
    
    if (!token || !user) {
      navigate('/admin/login');
      return;
    }
    
    setAdminUser(JSON.parse(user));
    loadDashboard();
  }, [location]);

  const loadDashboard = async () => {
    const token = localStorage.getItem('admin_token');
    
    try {
      // Get analytics
      const statsRes = await fetch(
        `${API_BASE_URL}/api/admin/events/analytics/dashboard?location=${location}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const statsData = await statsRes.json();
      setStats(statsData);
      
      // Get recent events
      const eventsRes = await fetch(
        `${API_BASE_URL}/api/admin/events?location=${location}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      const eventsData = await eventsRes.json();
      setEvents(eventsData.events.slice(0, 5));
    } catch (error) {
      console.error('Failed to load dashboard:', error);
      if (error.message.includes('401')) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F6F2] flex items-center justify-center">
        <p className="text-[#4C5A70]">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <>
      <title>Admin Dashboard | KGOB Events</title>
      
      <div className="min-h-screen bg-[#F7F6F2]">
        {/* Top Bar */}
        <header className="bg-[#0B2240] text-white">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-4">
                <h1 className="font-heading text-xl font-semibold">KGOB Events Admin</h1>
                
                {/* Location Switcher */}
                <select
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  className="bg-[#123059] text-white rounded-lg px-3 py-2 text-sm"
                  data-testid="admin-location-filter"
                >
                  <option value="all">All Locations</option>
                  <option value="charlotte">Charlotte</option>
                  <option value="mooresville">Mooresville</option>
                  <option value="winston-salem">Winston-Salem</option>
                </select>
              </div>
              
              <div className="flex items-center gap-4">
                <span className="text-sm">{adminUser?.first_name} ({adminUser?.email})</span>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-sm hover:text-[#E3C96E] transition-colors"
                  data-testid="admin-logout-btn"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-soft border border-[#E1DFD7]">
                <div className="flex items-center gap-3 mb-2">
                  <Calendar className="w-5 h-5 text-[#1FA3A3]" />
                  <span className="text-sm text-[#4C5A70]">Total Events</span>
                </div>
                <div className="text-3xl font-semibold text-[#0B2240]">{stats.total_events}</div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-soft border border-[#E1DFD7]">
                <div className="flex items-center gap-3 mb-2">
                  <Users className="w-5 h-5 text-[#1FA3A3]" />
                  <span className="text-sm text-[#4C5A70]">Total RSVPs</span>
                </div>
                <div className="text-3xl font-semibold text-[#0B2240]">{stats.total_rsvps}</div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-soft border border-[#E1DFD7]">
                <div className="flex items-center gap-3 mb-2">
                  <TrendingUp className="w-5 h-5 text-[#1FA3A3]" />
                  <span className="text-sm text-[#4C5A70]">Show Rate</span>
                </div>
                <div className="text-3xl font-semibold text-[#0B2240]">{stats.show_rate}%</div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-soft border border-[#E1DFD7]">
                <div className="flex items-center gap-3 mb-2">
                  <BarChart3 className="w-5 h-5 text-[#1FA3A3]" />
                  <span className="text-sm text-[#4C5A70]">Avg NPS</span>
                </div>
                <div className="text-3xl font-semibold text-[#0B2240]">{stats.avg_nps}</div>
              </div>
            </div>
          )}

          {/* Quick Actions */}
          <div className="bg-white rounded-xl p-6 shadow-soft border border-[#E1DFD7] mb-8">
            <h2 className="font-heading text-xl font-semibold text-[#0B2240] mb-4">Quick Actions</h2>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => navigate('/admin/events')}
                className="flex items-center gap-2 px-4 py-2 bg-[#0B2240] text-white rounded-lg hover:bg-[#0A1D35] transition-colors"
                data-testid="manage-events-btn"
              >
                <Plus className="w-4 h-4" />
                Manage Events
              </button>
              <button
                onClick={() => window.open('/events', '_blank')}
                className="px-4 py-2 border border-[#E1DFD7] text-[#0B2240] rounded-lg hover:bg-[#EDEBE6] transition-colors"
              >
                View Public Site ↗
              </button>
            </div>
          </div>

          {/* Recent Events */}
          <div className="bg-white rounded-xl p-6 shadow-soft border border-[#E1DFD7]">
            <h2 className="font-heading text-xl font-semibold text-[#0B2240] mb-4">Recent Events</h2>
            
            {events.length === 0 ? (
              <p className="text-[#4C5A70] text-center py-8">No events found</p>
            ) : (
              <div className="space-y-3">
                {events.map(event => (
                  <div
                    key={event.id}
                    onClick={() => navigate(`/admin/events/${event.id}/rsvps`)}
                    className="flex justify-between items-center p-4 border border-[#E1DFD7] rounded-lg hover:bg-[#F7F6F2] transition-colors cursor-pointer"
                    data-testid="event-row"
                  >
                    <div>
                      <h3 className="font-medium text-[#0B2240]">{event.title}</h3>
                      <p className="text-sm text-[#4C5A70]">
                        {new Date(event.start_datetime).toLocaleDateString()} • {event.location_city}
                      </p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-[#4C5A70]">
                        {event.total_rsvps}/{event.capacity || '∞'} RSVPs
                      </span>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                        event.visibility === 'published' ? 'bg-[#1FA3A3]/10 text-[#1FA3A3]' :
                        event.visibility === 'draft' ? 'bg-[#D4AF37]/10 text-[#D4AF37]' :
                        'bg-[#EDEBE6] text-[#4C5A70]'
                      }`}>
                        {event.visibility}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
